import { useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconClock, IconCoffee } from '@/components/icons'
import { ActionButton, AppModal } from '@/components/ui'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import { useSidebarShift } from '@/hooks/useSidebarShift'
import { useOnShiftUsers } from '@/lib/on-shift-users-context'
import { formatShiftStartedAtForDisplay, formatUserTimeFromIso } from '@/lib/user-datetime-format'
import type {
  ColleaguesTodayResponse,
  CurrentShiftResponse,
  MeUser,
  ShiftColleagueFactRow,
  ShiftColleagueJoiningSoonRow,
  ShiftColleaguePlannedRow,
} from '@/types/api'

export function ScheduleShiftsTodayCard({
  colleaguesLoading,
  colleaguesErr,
  colleaguesToday,
  onShiftMutated,
  /** false — только блок «Я» и кнопки смены (например, нет прав на табель) */
  showColleaguesList = true,
  /**
   * true — в табеле: без верхнего блока «Я» и кнопок смены; ниже — «График: …» и три колонки (как на дашборде).
   */
  hideShiftActions = false,
  /**
   * true — только колонка «На смене» по факту, без «Я» сверху и без плановых колонок (узкий режим).
   */
  colleaguesOnShiftOnly = false,
  compact = false,
  prominentActions = false,
  dashboardLayout = false,
  maxColleaguesHeight,
}: {
  colleaguesLoading?: boolean
  colleaguesErr: string | null
  colleaguesToday: ColleaguesTodayResponse | null
  onShiftMutated?: () => void
  showColleaguesList?: boolean
  hideShiftActions?: boolean
  colleaguesOnShiftOnly?: boolean
  compact?: boolean
  prominentActions?: boolean
  dashboardLayout?: boolean
  maxColleaguesHeight?: number
}) {
  const { user } = useAuth()
  const {
    shift,
    shiftInfo,
    planPrimary,
    planDetail,
    shiftBackend,
    busy,
    showBreak,
    breakLabel,
    breakInfo,
    toggleShift,
    toggleBreak,
  } = useSidebarShift(30_000, { onMutationSuccess: onShiftMutated })
  const { refresh: refreshOnShiftIds } = useOnShiftUsers()
  const [forceEndingUserId, setForceEndingUserId] = useState<string | null>(null)
  const [forceEndTarget, setForceEndTarget] = useState<ShiftColleagueFactRow | null>(null)

  const meta =
    colleaguesToday != null
      ? `${colleaguesToday.today_date} · ${colleaguesToday.schedule_timezone ?? colleaguesToday.workspace_timezone}`
      : null
  const onFactAll = colleaguesToday?.on_shift_fact ?? []
  const showMeOnFact = !colleaguesOnShiftOnly && shift.active && user
  /** Строки коллег в «На смене» — без дубля «Вы», если ниже рендерится MeOnShiftFactRow */
  const onFactListRaw = colleaguesOnShiftOnly || showMeOnFact ? onFactAll.filter((r) => r.user_id !== user?.id) : onFactAll
  const offPlanList = colleaguesOnShiftOnly ? [] : onFactListRaw.filter((r) => r.is_off_plan)
  const onFactList = colleaguesOnShiftOnly ? onFactListRaw : onFactListRaw.filter((r) => !r.is_off_plan)
  const joining = colleaguesToday?.planned_joining_within_hour ?? []
  const planned = colleaguesToday?.planned_not_started ?? []
  const canForceEndShifts = ['admin', 'owner'].includes(String(user?.role || '').toLowerCase())
  const showMeOffPlan = showMeOnFact && !!shiftBackend?.is_off_plan
  const plannedMeCount = !shift.active && user && hasPlanTodayFromShiftBackend(shiftBackend) ? 1 : 0
  const onShiftCount = onFactList.length + (showMeOnFact && !showMeOffPlan ? 1 : 0)
  const offPlanCount = offPlanList.length + (showMeOffPlan ? 1 : 0)
  const plannedCount = planned.length + plannedMeCount
  const listMaxHeight = maxColleaguesHeight ?? (compact ? 220 : undefined)
  const isBreakActive = !!shiftBackend?.is_on_break || breakLabel.includes('Закончить')
  const shiftActionLabel = shift.active ? 'Завершить смену' : 'Начать смену'
  const breakActionLabel = isBreakActive ? 'Завершить перерыв' : 'Начать перерыв'
  const shiftStatusColor = shiftBackend?.is_on_break ? 'orange' : shift.active ? 'green' : 'gray'
  const shiftStatusLabel = shiftBackend?.is_on_break ? 'На перерыве' : shift.active ? 'На смене' : 'Не на смене'

  const forceEndShift = async () => {
    const row = forceEndTarget
    if (!row) return
    if (!canForceEndShifts || forceEndingUserId) return
    const name = row.display_name || 'сотрудника'
    setForceEndingUserId(row.user_id)
    try {
      await api.forceEndShift(row.user_id)
      showNotification({
        title: 'Смена завершена',
        message: `Смена сотрудника ${name} принудительно завершена.`,
        color: 'green',
      })
      void refreshOnShiftIds()
      onShiftMutated?.()
      setForceEndTarget(null)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось завершить смену сотрудника',
        color: 'red',
      })
    } finally {
      setForceEndingUserId(null)
    }
  }

  const myBreakAt = shiftBackend?.is_on_break ? formatUserTimeFromIso(shiftBackend.break_started_at) : null
  const onBreakDigest = useMemo(() => {
    const raw = colleaguesToday?.on_shift_fact ?? []
    const factOthers = user ? raw.filter((r) => r.user_id !== user?.id) : raw
    const parts: string[] = []
    if (!colleaguesOnShiftOnly && shift.active && user && shiftBackend?.is_on_break) {
      parts.push(myBreakAt ? `вы (с ${myBreakAt})` : 'вы')
    }
    for (const r of factOthers) {
      if (!r.is_on_break) continue
      const t = formatUserTimeFromIso(r.break_started_at)
      const nm = r.display_name || '—'
      parts.push(t ? `${nm} (с ${t})` : nm)
    }
    return parts
  }, [
    colleaguesOnShiftOnly,
    shift.active,
    user,
    shiftBackend?.is_on_break,
    shiftBackend?.break_started_at,
    myBreakAt,
    colleaguesToday,
  ])

  const forceEndTargetName = forceEndTarget?.display_name || 'сотрудника'

  return (
    <>
      <Card withBorder padding={compact ? 'xs' : 'sm'} radius="md" variant="light">
        <Stack gap={compact ? 'xs' : 'sm'}>
          {(!hideShiftActions || (showColleaguesList && colleaguesLoading)) && (
            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
              <Group gap="xs" align="center" wrap="wrap">
                {!hideShiftActions ? (
                  <Title order={compact ? 6 : 5}>{colleaguesOnShiftOnly ? 'Кто на смене' : 'Смены сегодня'}</Title>
                ) : (
                  <span />
                )}
                {!hideShiftActions ? (
                  <Badge size="sm" variant="light" color={shiftStatusColor} radius="sm">
                    {shiftStatusLabel}
                  </Badge>
                ) : null}
              </Group>
              {showColleaguesList && colleaguesLoading ? (
                <Text size="xs" c="dimmed">
                  Обновление списка…
                </Text>
              ) : null}
            </Group>
          )}

          {!colleaguesOnShiftOnly && !hideShiftActions ? (
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Stack gap={compact ? 1 : 2} style={{ minWidth: 0, flex: '1 1 200px' }}>
                <Text size="xs" fw={600} c="dimmed">
                  Я
                </Text>
                <Text size="sm">{shiftInfo}</Text>
                {planPrimary ? (
                  <Text size="xs" c="dimmed">
                    {planPrimary}
                  </Text>
                ) : null}
                {planDetail ? (
                  <Text size="xs" c="dimmed">
                    {planDetail}
                  </Text>
                ) : null}
                {breakInfo ? (
                  <Text size="xs" c="dimmed">
                    {breakInfo}
                  </Text>
                ) : null}
                {shiftBackend?.is_on_break ? (
                  <Text size="xs" c="orange" fw={500}>
                    {myBreakAt ? `На перерыве с ${myBreakAt}` : 'На перерыве'}
                  </Text>
                ) : null}
              </Stack>
              <Group gap="xs" wrap="wrap" style={{ flex: prominentActions ? '1 1 260px' : undefined, justifyContent: 'flex-end' }}>
                <Tooltip
                  label={shift.active ? 'Закрыть текущую смену вручную' : 'Открыть смену и начать работу с обращениями'}
                  withArrow
                  multiline
                  w={260}
                >
                  <Button
                    size={prominentActions ? 'sm' : 'compact-sm'}
                    leftSection={<IconClock size={prominentActions ? 17 : 14} stroke={1.5} />}
                    variant={shift.active ? 'light' : 'filled'}
                    color={shift.active ? 'red' : 'blue'}
                    loading={busy}
                    onClick={() => void toggleShift()}
                    style={prominentActions ? { minHeight: 42, minWidth: 158, fontWeight: 700 } : undefined}
                  >
                    {shiftActionLabel}
                  </Button>
                </Tooltip>
                {showBreak ? (
                  <Tooltip
                    label={
                      isBreakActive
                        ? 'Вернуться к работе до истечения лимита перерыва'
                        : 'Начать перерыв в рамках доступного лимита на сегодня'
                    }
                    withArrow
                    multiline
                    w={260}
                  >
                    <Button
                      size={prominentActions ? 'sm' : 'compact-sm'}
                      leftSection={<IconCoffee size={prominentActions ? 17 : 14} stroke={1.5} />}
                      variant={isBreakActive ? 'filled' : 'light'}
                      color={isBreakActive ? 'orange' : 'gray'}
                      loading={busy}
                      onClick={() => void toggleBreak()}
                      style={prominentActions ? { minHeight: 42, minWidth: 166, fontWeight: 700 } : undefined}
                    >
                      {breakActionLabel}
                    </Button>
                  </Tooltip>
                ) : null}
              </Group>
            </Group>
          ) : null}

          {showColleaguesList ? (
            <>
              {!colleaguesOnShiftOnly && !hideShiftActions ? <Divider my={2} /> : null}
              {meta ? (
                <Text size="xs" c="dimmed">
                  {colleaguesOnShiftOnly ? meta : `График: ${meta}`}
                </Text>
              ) : null}
              {colleaguesErr ? (
                <Alert color="orange" p="xs" title="Коллеги">
                  <Text size="xs">{colleaguesErr}</Text>
                </Alert>
              ) : null}

              {dashboardLayout && !colleaguesOnShiftOnly ? (
                <Stack gap="xs">
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                    <ShiftColumn title="На смене" count={onShiftCount} maxHeight={listMaxHeight} minHeight={148} polished tone="active">
                      {onBreakDigest.length > 0 ? (
                        <Text size="xs" c="orange" lh={1.35}>
                          Сейчас на перерыве: {onBreakDigest.join('; ')}
                        </Text>
                      ) : null}
                      {!colleaguesErr && colleaguesToday && onFactList.length === 0 && !(shift.active && user) ? (
                        <Text size="xs" c="dimmed">
                          Нет.
                        </Text>
                      ) : null}
                      {showMeOnFact && !showMeOffPlan ? <MeOnShiftFactRow user={user} backend={shiftBackend} /> : null}
                      {onFactList.map((row) => (
                        <ColleagueFactRow
                          key={row.user_id}
                          row={row}
                          canForceEnd={canForceEndShifts}
                          forceEnding={forceEndingUserId === row.user_id}
                          forceEndDisabled={forceEndingUserId != null}
                          onForceEnd={() => setForceEndTarget(row)}
                        />
                      ))}
                    </ShiftColumn>

                    <ShiftColumn title="До старта ≤ 1 ч" count={joining.length} maxHeight={listMaxHeight} minHeight={148} polished tone="soon">
                      {!colleaguesErr && colleaguesToday && joining.length === 0 ? (
                        <Text size="xs" c="dimmed">
                          Нет.
                        </Text>
                      ) : null}
                      {joining.map((row) => (
                        <ColleagueJoiningSoonRow key={row.user_id} row={row} />
                      ))}
                    </ShiftColumn>
                  </SimpleGrid>

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                    <ShiftColumn title="По плану сегодня" count={plannedCount} maxHeight={listMaxHeight} minHeight={148} polished tone="planned">
                      {!colleaguesErr &&
                      colleaguesToday &&
                      planned.length === 0 &&
                      !(!shift.active && user && hasPlanTodayFromShiftBackend(shiftBackend)) ? (
                        <Text size="xs" c="dimmed">
                          Нет.
                        </Text>
                      ) : null}
                      {!shift.active && user && hasPlanTodayFromShiftBackend(shiftBackend) ? (
                        <MePlannedNotStartedRow user={user} backend={shiftBackend} />
                      ) : null}
                      {planned.map((row) => (
                        <ColleaguePlannedRow key={row.user_id} row={row} />
                      ))}
                    </ShiftColumn>

                    <ShiftColumn title="На смене вне плана" count={offPlanCount} maxHeight={listMaxHeight} minHeight={148} polished tone="offPlan">
                      {!colleaguesErr && colleaguesToday && offPlanCount === 0 ? (
                        <Text size="xs" c="dimmed">
                          Нет.
                        </Text>
                      ) : null}
                      {showMeOffPlan && user ? (
                        <MeOnShiftFactRow user={user} backend={shiftBackend} showOffPlanReason />
                      ) : null}
                      {offPlanList.map((row) => (
                        <ColleagueFactRow
                          key={row.user_id}
                          row={row}
                          canForceEnd={canForceEndShifts}
                          forceEnding={forceEndingUserId === row.user_id}
                          forceEndDisabled={forceEndingUserId != null}
                          onForceEnd={() => setForceEndTarget(row)}
                          showOffPlanReason
                        />
                      ))}
                    </ShiftColumn>
                  </SimpleGrid>
                </Stack>
              ) : (
              <SimpleGrid cols={{ base: 1, sm: colleaguesOnShiftOnly ? 1 : 2, lg: colleaguesOnShiftOnly ? 1 : 4 }} spacing="xs" verticalSpacing="sm">
                <ShiftColumn title="На смене" count={onShiftCount} maxHeight={listMaxHeight} minHeight={compact ? 118 : undefined}>
                  {onBreakDigest.length > 0 ? (
                    <Text size="xs" c="orange" lh={1.35}>
                      Сейчас на перерыве: {onBreakDigest.join('; ')}
                    </Text>
                  ) : null}
                  {!colleaguesErr &&
                  colleaguesToday &&
                  onFactList.length === 0 &&
                  (colleaguesOnShiftOnly || !(shift.active && user)) ? (
                    <Text size="xs" c="dimmed">
                      Нет.
                    </Text>
                  ) : null}
                  {showMeOnFact && !showMeOffPlan ? <MeOnShiftFactRow user={user} backend={shiftBackend} /> : null}
                  {onFactList.map((row) => (
                    <ColleagueFactRow
                      key={row.user_id}
                      row={row}
                      canForceEnd={canForceEndShifts}
                      forceEnding={forceEndingUserId === row.user_id}
                      forceEndDisabled={forceEndingUserId != null}
                      onForceEnd={() => setForceEndTarget(row)}
                    />
                  ))}
                </ShiftColumn>

                {!colleaguesOnShiftOnly ? (
                  <ShiftColumn title="На смене вне плана" count={offPlanCount} maxHeight={listMaxHeight}>
                    {!colleaguesErr && colleaguesToday && offPlanCount === 0 ? (
                      <Text size="xs" c="dimmed">
                        Нет.
                      </Text>
                    ) : null}
                    {showMeOffPlan && user ? (
                      <MeOnShiftFactRow user={user} backend={shiftBackend} showOffPlanReason />
                    ) : null}
                    {offPlanList.map((row) => (
                      <ColleagueFactRow
                        key={row.user_id}
                        row={row}
                        canForceEnd={canForceEndShifts}
                        forceEnding={forceEndingUserId === row.user_id}
                        forceEndDisabled={forceEndingUserId != null}
                        onForceEnd={() => setForceEndTarget(row)}
                        showOffPlanReason
                      />
                    ))}
                  </ShiftColumn>
                ) : null}

                {!colleaguesOnShiftOnly ? (
                  <ShiftColumn title="До старта ≤ 1 ч" count={joining.length} maxHeight={listMaxHeight}>
                    {!colleaguesErr && colleaguesToday && joining.length === 0 ? (
                      <Text size="xs" c="dimmed">
                        Нет.
                      </Text>
                    ) : null}
                    {joining.map((row) => (
                      <ColleagueJoiningSoonRow key={row.user_id} row={row} />
                    ))}
                  </ShiftColumn>
                ) : null}
                {!colleaguesOnShiftOnly ? (
                  <ShiftColumn title="По плану сегодня" count={plannedCount} maxHeight={listMaxHeight}>
                    {!colleaguesErr &&
                    colleaguesToday &&
                    planned.length === 0 &&
                    !(!shift.active && user && hasPlanTodayFromShiftBackend(shiftBackend)) ? (
                      <Text size="xs" c="dimmed">
                        Нет.
                      </Text>
                    ) : null}
                    {!shift.active && user && hasPlanTodayFromShiftBackend(shiftBackend) ? (
                      <MePlannedNotStartedRow user={user} backend={shiftBackend} />
                    ) : null}
                    {planned.map((row) => (
                      <ColleaguePlannedRow key={row.user_id} row={row} />
                    ))}
                  </ShiftColumn>
                ) : null}
              </SimpleGrid>
              )}
            </>
          ) : null}
        </Stack>
      </Card>
      <AppModal
        opened={!!forceEndTarget}
        onClose={() => setForceEndTarget(null)}
        title="Завершить смену сотрудника"
        size="md"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setForceEndTarget(null)} disabled={!!forceEndingUserId} />
            <ActionButton
              action="delete"
              label="Завершить смену"
              loading={!!forceEndingUserId}
              onClick={() => void forceEndShift()}
            />
          </>
        }
      >
        <Stack gap="sm">
          <Text size="sm">
            Принудительно завершить смену для{' '}
            <Text span fw={600}>
              {forceEndTargetName}
            </Text>
            ?
          </Text>
          <Text size="sm" c="dimmed">
            Смена будет закрыта текущим временем. В чат уйдёт уведомление о принудительном завершении от имени
            администратора.
          </Text>
        </Stack>
      </AppModal>
    </>
  )
}

function ShiftColumn({
  title,
  count,
  maxHeight,
  minHeight,
  polished = false,
  tone = 'active',
  children,
}: {
  title: string
  count: number
  maxHeight?: number
  minHeight?: number
  polished?: boolean
  tone?: 'active' | 'soon' | 'planned' | 'offPlan'
  children: ReactNode
}) {
  const toneColor =
    tone === 'active'
      ? 'green'
      : tone === 'soon'
        ? 'blue'
        : tone === 'offPlan'
          ? 'orange'
          : 'gray'
  const accent =
    tone === 'active'
      ? 'var(--mantine-color-green-6)'
      : tone === 'soon'
        ? 'var(--mantine-color-brand-filled)'
        : tone === 'offPlan'
          ? 'var(--mantine-color-orange-6)'
          : 'var(--mantine-color-gray-6)'
  const background = polished
    ? tone === 'offPlan'
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-orange-light) 72%, var(--mantine-color-body)), light-dark(#ffffff, var(--mantine-color-dark-7)))'
      : tone === 'active'
        ? 'linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-green-light) 62%, var(--mantine-color-body)), light-dark(#ffffff, var(--mantine-color-dark-7)))'
        : tone === 'soon'
          ? 'linear-gradient(135deg, var(--mantine-color-brand-light), light-dark(#ffffff, var(--mantine-color-dark-7)))'
          : 'linear-gradient(135deg, light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6)), light-dark(#ffffff, var(--mantine-color-dark-7)))'
    : 'var(--mantine-color-body)'

  return (
    <Stack gap={6} style={{ minWidth: 0, height: polished ? '100%' : undefined }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          border: polished
            ? '1px solid color-mix(in srgb, var(--mantine-color-default-border) 78%, transparent)'
            : '1px solid var(--mantine-color-default-border)',
          borderRadius: 8,
          background,
          padding: polished ? 10 : 8,
          minHeight,
          height: polished ? '100%' : undefined,
          boxShadow: polished
            ? '0 1px 2px color-mix(in srgb, var(--mantine-color-black) 5%, transparent)'
            : undefined,
        }}
      >
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            {polished ? (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 22,
                  borderRadius: 999,
                  background: accent,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Text size="xs" fw={800} truncate="end" style={{ letterSpacing: 0, textTransform: polished ? 'uppercase' : undefined }}>
              {title}
            </Text>
          </Group>
          <Badge size="sm" variant={polished ? 'filled' : 'light'} color={count > 0 ? toneColor : 'gray'} radius="sm">
            {count}
          </Badge>
        </Group>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minHeight: 24,
            maxHeight,
            overflowY: maxHeight ? 'auto' : undefined,
            paddingRight: maxHeight ? 4 : undefined,
          }}
        >
          {children}
        </div>
      </div>
    </Stack>
  )
}

function hasPlanTodayFromShiftBackend(b: CurrentShiftResponse | null): boolean {
  if (!b) return false
  const m = b.planned_shift_minutes_today
  if (typeof m === 'number' && m > 0) return true
  return !!(
    b.planned_start_local ||
    b.planned_end_local ||
    (b.planned_intervals_label && b.planned_intervals_label.trim())
  )
}

function MeOnShiftFactRow({
  user,
  backend,
  showOffPlanReason = false,
}: {
  user: MeUser
  backend: CurrentShiftResponse | null
  showOffPlanReason?: boolean
}) {
  const display = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || '—'
  const src =
    resolveOperatorAvatarSrc({
      operator_name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email,
      operator_email: user.email,
      avatar_url: user.avatar_url,
    }) || ''
  const started = formatShiftStartedAtForDisplay(backend?.started_at ?? null)
  const until = backend?.planned_end_local
  const breakAt = backend?.is_on_break ? formatUserTimeFromIso(backend?.break_started_at ?? null) : null
  const detail = [
    started ? `с ${started}` : 'смена открыта',
    until ? `до ${until}` : null,
    backend?.is_on_break ? (breakAt ? `перерыв с ${breakAt}` : 'перерыв') : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const offPlanReason =
    backend?.off_plan_reason === 'no_plan'
      ? 'нет плановой смены'
      : backend?.off_plan_reason === 'outside_plan'
        ? 'вне планового интервала'
        : null
  const planDetail = backend?.planned_intervals_label ? `план ${backend.planned_intervals_label}` : null
  const offPlanDetail = [offPlanReason, planDetail].filter(Boolean).join(' · ')
  const title = backend?.planned_intervals_label || undefined

  return (
    <Group gap={6} wrap="nowrap" align="center" title={title} style={{ minWidth: 0 }}>
      <ShiftPresenceDot userId={user.id} forceOn size={7} />
      <Avatar src={src || undefined} size={22} radius="xl">
        {(display || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Text size="xs" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
        <Text component="span" fw={600}>
          Вы
        </Text>
        <Text component="span" c="dimmed">
          {' '}
          · {detail}
        </Text>
        {showOffPlanReason && offPlanDetail ? (
          <Text component="span" c="orange">
            {' '}
            · {offPlanDetail}
          </Text>
        ) : null}
      </Text>
    </Group>
  )
}

function MePlannedNotStartedRow({ user, backend }: { user: MeUser; backend: CurrentShiftResponse | null }) {
  const display = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || '—'
  const src =
    resolveOperatorAvatarSrc({
      operator_name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email,
      operator_email: user.email,
      avatar_url: user.avatar_url,
    }) || ''
  const start = backend?.planned_start_local
  const end = backend?.planned_end_local
  const label = (backend?.planned_intervals_label || '').trim()
  let plan = '—'
  if (start && end) plan = `${start}–${end}`
  else if (end) plan = `до ${end}`
  else if (start) plan = `с ${start}`
  else if (label) plan = label
  const title = backend?.planned_intervals_label || undefined

  return (
    <Group gap={6} wrap="nowrap" align="center" title={title} style={{ minWidth: 0 }}>
      <ShiftPresenceDot userId={user.id} size={7} />
      <Avatar src={src || undefined} size={22} radius="xl">
        {(display || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Text size="xs" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
        <Text component="span" fw={600}>
          Вы
        </Text>
        <Text component="span" c="dimmed">
          {' '}
          · {plan}
        </Text>
      </Text>
    </Group>
  )
}

function ColleagueFactRow({
  row,
  canForceEnd,
  forceEnding,
  forceEndDisabled,
  onForceEnd,
  showOffPlanReason = false,
}: {
  row: ShiftColleagueFactRow
  canForceEnd?: boolean
  forceEnding?: boolean
  forceEndDisabled?: boolean
  onForceEnd?: () => void
  showOffPlanReason?: boolean
}) {
  const src =
    resolveOperatorAvatarSrc({
      operator_name: row.display_name,
      operator_email: row.email,
      avatar_url: row.avatar_url,
    }) || ''
  const started = formatShiftStartedAtForDisplay(row.shift_started_at)
  const until = row.planned_end_local
  const breakAt = row.is_on_break ? formatUserTimeFromIso(row.break_started_at) : null
  const detail = [
    started ? `с ${started}` : 'открыта',
    until ? `до ${until}` : null,
    row.is_on_break ? (breakAt ? `перерыв с ${breakAt}` : 'перерыв') : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const offPlanReason =
    row.off_plan_reason === 'no_plan'
      ? 'нет плановой смены'
      : row.off_plan_reason === 'outside_plan'
        ? 'вне планового интервала'
        : null
  const planDetail = row.planned_intervals_label ? `план ${row.planned_intervals_label}` : null
  const offPlanDetail = [offPlanReason, planDetail].filter(Boolean).join(' · ')
  const title = row.planned_intervals_label || undefined

  return (
    <Group gap={6} wrap="nowrap" align="center" title={title} style={{ minWidth: 0 }}>
      <ShiftPresenceDot userId={row.user_id} forceOn size={7} />
      <Avatar src={src || undefined} size={22} radius="xl">
        {(row.display_name || '?').charAt(0)}
      </Avatar>
      <Text size="xs" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
        <Text component="span" fw={600}>
          {row.display_name || '—'}
        </Text>
        <Text component="span" c="dimmed">
          {' '}
          · {detail}
        </Text>
        {showOffPlanReason && offPlanDetail ? (
          <Text component="span" c="orange">
            {' '}
            · {offPlanDetail}
          </Text>
        ) : null}
      </Text>
      {canForceEnd ? (
        <Button
          size="compact-xs"
          variant="subtle"
          color="red"
          loading={forceEnding}
          disabled={forceEndDisabled && !forceEnding}
          onClick={onForceEnd}
        >
          Завершить
        </Button>
      ) : null}
    </Group>
  )
}

function formatMinutesUntilStartRu(n: number): string {
  if (n <= 0) return 'Начало по плану сейчас'
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return `Через ${n} минут`
  if (mod10 === 1) return `Через ${n} минуту`
  if (mod10 >= 2 && mod10 <= 4) return `Через ${n} минуты`
  return `Через ${n} минут`
}

function ColleagueJoiningSoonRow({ row }: { row: ShiftColleagueJoiningSoonRow }) {
  const when = formatMinutesUntilStartRu(row.minutes_until_start)
  const start = row.planned_start_local
  const detail = [when, start ? `с ${start}` : null].filter(Boolean).join(' · ')

  return (
    <Group
      gap={6}
      wrap="nowrap"
      align="center"
      title={row.planned_intervals_label || undefined}
      style={{ minWidth: 0 }}
    >
      <ShiftPresenceDot userId={row.user_id} size={7} />
      <Avatar
        src={resolveOperatorAvatarSrc({
          operator_name: row.display_name,
          operator_email: row.email,
          avatar_url: row.avatar_url,
        })}
        size={22}
        radius="xl"
      >
        {(row.display_name || '?').charAt(0)}
      </Avatar>
      <Text size="xs" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
        <Text component="span" fw={600}>
          {row.display_name || '—'}
        </Text>
        <Text component="span" c="dimmed">
          {' '}
          · {detail}
        </Text>
      </Text>
    </Group>
  )
}

function ColleaguePlannedRow({ row }: { row: ShiftColleaguePlannedRow }) {
  const start = row.planned_start_local
  const end = row.planned_end_local
  const label = (row.planned_intervals_label || '').trim()
  let plan = '—'
  if (start && end) plan = `${start}–${end}`
  else if (end) plan = `до ${end}`
  else if (start) plan = `с ${start}`
  else if (label) plan = label

  return (
    <Group
      gap={6}
      wrap="nowrap"
      align="center"
      title={row.planned_intervals_label || undefined}
      style={{ minWidth: 0 }}
    >
      <ShiftPresenceDot userId={row.user_id} size={7} />
      <Avatar
        src={resolveOperatorAvatarSrc({
          operator_name: row.display_name,
          operator_email: row.email,
          avatar_url: row.avatar_url,
        })}
        size={22}
        radius="xl"
      >
        {(row.display_name || '?').charAt(0)}
      </Avatar>
      <Text size="xs" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
        <Text component="span" fw={600}>
          {row.display_name || '—'}
        </Text>
        <Text component="span" c="dimmed">
          {' '}
          · {plan}
        </Text>
      </Text>
    </Group>
  )
}
